import { Trash2 } from "lucide-react";
import {
  useCreate,
  useDelete,
  useGetIdentity,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useEffect, useId, useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  DateField,
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "./FormDialog";
import {
  OPPORTUNITY_STAGES,
  OPPORTUNITY_TYPES,
  opportunityStageLabelKey,
  opportunityTypeLabelKey,
} from "./labels";
import {
  normalizeOpportunityForm,
  opportunityFormDefaults,
  type OpportunityFormValues,
} from "./opportunities";
import type { MarketOpportunity } from "./types";

interface OpportunityFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edits this opportunity; creates a new one when absent. */
  opportunity?: MarketOpportunity | null;
}

type TextKey = Exclude<keyof OpportunityFormValues, "type" | "stage">;

/** Create / edit / delete an opportunity in a ResponsiveDialog. */
export const OpportunityForm = ({
  open,
  onOpenChange,
  opportunity,
}: OpportunityFormProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const formId = useId();
  const [values, setValues] = useState(() =>
    opportunityFormDefaults(opportunity ?? undefined),
  );
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [create, { isPending: creating }] = useCreate<MarketOpportunity>();
  const [update, { isPending: updating }] = useUpdate<MarketOpportunity>();
  const [remove, { isPending: deleting }] = useDelete<MarketOpportunity>();

  useEffect(() => {
    if (open) {
      setValues(opportunityFormDefaults(opportunity ?? undefined));
      setError(null);
      setConfirmDelete(false);
    }
  }, [open, opportunity]);

  const set =
    <K extends keyof OpportunityFormValues>(key: K) =>
    (value: OpportunityFormValues[K]) => {
      setValues((current) => ({ ...current, [key]: value }));
      setError(null);
    };
  const close = () => onOpenChange(false);
  const onError = () => notify("market.opportunities.error", { type: "error" });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeOpportunityForm(values);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (opportunity) {
      update(
        "market_opportunities",
        { id: opportunity.id, data: result.data, previousData: opportunity },
        {
          mutationMode: "optimistic",
          onSuccess: () =>
            notify("market.opportunities.updated", { type: "success" }),
          onError,
        },
      );
      close();
      return;
    }
    create(
      "market_opportunities",
      // The DB trigger sets the owner too; sending it keeps the demo honest.
      { data: { ...result.data, sales_id: identity?.id ?? null } },
      {
        onSuccess: () => {
          notify("market.opportunities.created", { type: "success" });
          close();
        },
        onError,
      },
    );
  };

  const onDelete = () => {
    if (!opportunity) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    remove(
      "market_opportunities",
      { id: opportunity.id, previousData: opportunity },
      {
        mutationMode: "optimistic",
        onSuccess: () =>
          notify("market.opportunities.deleted", { type: "info" }),
        onError,
      },
    );
    close();
  };

  const errorId = `${formId}-error`;
  const fieldError = (keys: string[]) =>
    error && keys.includes(error) ? (
      <FieldError id={errorId}>{translate(error)}</FieldError>
    ) : null;
  const invalid = (keys: string[]) =>
    error && keys.includes(error)
      ? { "aria-invalid": true, "aria-describedby": errorId }
      : {};
  const NAME_ERRORS = [
    "market.opportunities.form.name_required",
    "market.opportunities.form.name_too_long",
  ];
  const VALUE_ERRORS = ["market.opportunities.form.value_invalid"];
  const DATE_ERRORS = ["market.opportunities.form.date_invalid"];

  const text = (key: TextKey) => (
    <Field
      id={`${formId}-${key}`}
      label={translate(`market.opportunities.form.${key}`)}
    >
      <Input
        id={`${formId}-${key}`}
        value={values[key]}
        onChange={(event) => set(key)(event.target.value)}
      />
    </Field>
  );

  const busy = creating || updating || deleting;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(
        opportunity ? "market.opportunities.edit" : "market.opportunities.new",
      )}
      formId={formId}
      onSubmit={submit}
      actions={
        <>
          {opportunity ? (
            <Button
              type="button"
              variant="destructive"
              onClick={onDelete}
              disabled={busy}
              className="mr-auto rounded-full max-md:h-12"
            >
              <Trash2 aria-hidden className="size-4" />
              {translate(
                confirmDelete
                  ? "market.opportunities.delete_confirm"
                  : "market.opportunities.delete",
              )}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            className="rounded-full max-md:hidden"
            onClick={close}
          >
            {translate("market.opportunities.form.cancel")}
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={busy}
            className="btn-save rounded-full px-5 max-md:h-12 max-md:flex-1"
          >
            {translate("market.opportunities.form.save")}
          </Button>
        </>
      }
    >
      <Field
        id={`${formId}-name`}
        label={translate("market.opportunities.form.name")}
      >
        <Input
          id={`${formId}-name`}
          value={values.name}
          onChange={(event) => set("name")(event.target.value)}
          autoFocus={!isMobile}
          maxLength={240}
          {...invalid(NAME_ERRORS)}
        />
        {fieldError(NAME_ERRORS)}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field
          id={`${formId}-type`}
          label={translate("market.opportunities.form.type")}
        >
          <select
            id={`${formId}-type`}
            className={SELECT_CLASS}
            value={values.type}
            onChange={(event) =>
              set("type")(event.target.value as OpportunityFormValues["type"])
            }
          >
            {OPPORTUNITY_TYPES.map((type) => (
              <option key={type} value={type}>
                {translate(opportunityTypeLabelKey(type))}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id={`${formId}-stage`}
          label={translate("market.opportunities.form.stage")}
        >
          <select
            id={`${formId}-stage`}
            className={SELECT_CLASS}
            value={values.stage}
            onChange={(event) =>
              set("stage")(event.target.value as OpportunityFormValues["stage"])
            }
          >
            {OPPORTUNITY_STAGES.map((stage) => (
              <option key={stage} value={stage}>
                {translate(opportunityStageLabelKey(stage))}
              </option>
            ))}
          </select>
        </Field>
        {text("sector")}
        <Field
          id={`${formId}-estimated_value`}
          label={translate("market.opportunities.form.estimated_value")}
        >
          <Input
            id={`${formId}-estimated_value`}
            inputMode="decimal"
            value={values.estimated_value}
            onChange={(event) => set("estimated_value")(event.target.value)}
            {...invalid(VALUE_ERRORS)}
          />
          {fieldError(VALUE_ERRORS)}
        </Field>
      </div>
      {text("contact")}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {text("next_step")}
        <Field
          id={`${formId}-next_step_date`}
          label={translate("market.opportunities.form.next_step_date")}
        >
          <DateField
            id={`${formId}-next_step_date`}
            value={values.next_step_date}
            onChange={(event) => set("next_step_date")(event.target.value)}
            {...invalid(DATE_ERRORS)}
          />
          {fieldError(DATE_ERRORS)}
        </Field>
      </div>
      <Field
        id={`${formId}-notes`}
        label={translate("market.opportunities.form.notes")}
      >
        <Textarea
          id={`${formId}-notes`}
          value={values.notes}
          onChange={(event) => set("notes")(event.target.value)}
          rows={4}
        />
      </Field>
    </FormDialog>
  );
};
